"use client";

import Link from 'next/link';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Minus, Plus, Trash2, ArrowRight, ShoppingCart, Loader2, AlertCircle, Star } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useEffect, useState, useMemo } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '@/lib/firebase';
import type { DeliverySettings, User as AppUser, Category } from '@/lib/types';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { onAuthStateChanged } from 'firebase/auth';


export default function CartPage() {
  const { cartItems, removeFromCart, updateQuantity, clearCart, getItemStock } = useCart();
  const [isLoading, setIsLoading] = useState(true);
  const [appUser, setAppUser] = useState<AppUser | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  useEffect(() => {
    const authUnsubscribe = onAuthStateChanged(auth, async (user) => {
        if (user) {
            const userDoc = await getDoc(doc(db, 'users', user.uid));
            if (userDoc.exists()) {
                setAppUser(userDoc.data() as AppUser);
            }
        } else {
            setAppUser(null);
        }
        setIsAuthLoading(false);
    });

    const fetchInitialData = async () => {
      setIsLoading(true);
      // Podemos carregar dados aqui se necessário
      setIsLoading(false);
    };
    
    fetchInitialData();

    return () => authUnsubscribe();
  }, []);

  const isVip = appUser?.isVip || false;

  const { subtotal, vipDiscount } = useMemo(() => {
    let regularSubtotal = 0;
    let finalSubtotal = 0;

    cartItems.forEach(item => {
        const regularPrice = item.originalPrice || item.price;
        let currentPrice = item.price;

        if (isVip) {
            const vipPrice = item.variantId 
                ? item.variants.find(v => v.id === item.variantId)?.vipPrice
                : item.vipPrice;
            
            if (vipPrice && vipPrice > 0 && vipPrice < currentPrice) {
                currentPrice = vipPrice;
            }
        }
        
        regularSubtotal += regularPrice * item.quantity;
        finalSubtotal += currentPrice * item.quantity;
    });

    return {
        subtotal: regularSubtotal,
        vipDiscount: Math.max(0, regularSubtotal - finalSubtotal)
    };
  }, [cartItems, isVip]);

  const total = subtotal - vipDiscount;

  const outOfStockItems = cartItems.filter(item => {
    const stock = getItemStock(item);
    return stock !== undefined && item.quantity > stock;
  });

  if ((isLoading || isAuthLoading) && cartItems.length > 0) {
    return (
        <div className="container mx-auto max-w-6xl py-12 px-4 text-center">
           <Loader2 className="mx-auto h-24 w-24 animate-spin text-primary" />
        </div>
    )
  }

  if (cartItems.length === 0) {
    return (
        <div className="container mx-auto max-w-6xl py-12 px-4 text-center">
            <ShoppingCart className="mx-auto h-24 w-24 text-muted-foreground" />
            <h1 className="text-3xl font-bold font-headline mt-8 mb-4">Seu Carrinho está Vazio</h1>
            <p className="text-muted-foreground mb-8">Parece que você ainda não adicionou nenhum produto. Que tal dar uma olhada nas nossas ofertas?</p>
            <Button asChild>
                <Link href="/products">
                    Ver Produtos
                </Link>
            </Button>
        </div>
    );
  }

  const checkoutLink = '/checkoutfast';

  return (
    <div className="container mx-auto max-w-6xl py-12 px-4">
      <h1 className="text-3xl font-bold font-headline mb-8">Seu Carrinho</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {outOfStockItems.length > 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Itens Indisponíveis</AlertTitle>
              <AlertDescription>
                Alguns itens no seu carrinho não têm estoque suficiente. Por favor, ajuste as quantidades ou remova-os para continuar.
              </AlertDescription>
            </Alert>
          )}
          {cartItems.map(item => {
            const maxStock = getItemStock(item);
            const isItemOutOfStock = maxStock !== undefined && item.quantity > maxStock;
            const variantName = item.variantId ? item.variants?.find(v => v.id === item.variantId)?.options.name : null;

            return (
                <Card key={item.id + (item.variantId || '')} className={`flex flex-col sm:flex-row items-center p-4 gap-4 ${isItemOutOfStock ? 'border-destructive ring-2 ring-destructive/50' : ''}`}>
                    <div className="flex items-center w-full sm:w-auto gap-4">
                        <Image 
                            src={item.imageUrl || 'https://placehold.co/100x100.png'}
                            alt={item.name} 
                            width={80} 
                            height={80} 
                            className="rounded-md object-cover flex-shrink-0"
                        />
                        <div className="flex-grow">
                            <h2 className="font-semibold text-sm sm:text-base leading-tight">
                                {item.name}
                            </h2>
                            {variantName && (
                                <p className="text-xs font-bold text-primary uppercase mt-0.5">Opção: {variantName}</p>
                            )}
                            <p className="text-sm text-muted-foreground mt-1">R$ {item.price.toFixed(2)}</p>
                            {isItemOutOfStock && <p className="text-xs text-destructive mt-1 font-bold">Apenas {maxStock} em estoque!</p>}
                        </div>
                        <Button 
                            variant="ghost" 
                            size="icon" 
                            className="sm:hidden text-muted-foreground hover:text-destructive h-8 w-8" 
                            onClick={() => removeFromCart(item.id, item.variantId)}
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>
                    
                    <div className="flex items-center justify-between w-full sm:w-auto sm:ml-auto gap-4">
                        <div className="flex items-center gap-2">
                            <Button 
                                variant="outline" 
                                size="icon" 
                                className="h-8 w-8" 
                                onClick={() => updateQuantity(item.id, item.quantity - 1, item.variantId)} 
                                disabled={item.quantity <= 1}
                            >
                                <Minus className="h-4 w-4" />
                            </Button>
                            <span className="w-6 text-center text-sm font-medium">{item.quantity}</span>
                            <Button 
                                variant="outline" 
                                size="icon" 
                                className="h-8 w-8" 
                                onClick={() => updateQuantity(item.id, item.quantity + 1, item.variantId)} 
                                disabled={maxStock !== undefined && item.quantity >= maxStock}
                            >
                                <Plus className="h-4 w-4" />
                            </Button>
                        </div>
                        
                        <div className="flex items-center gap-4">
                            <div className="font-bold text-right sm:w-24 whitespace-nowrap">
                                R$ {(item.price * item.quantity).toFixed(2)}
                            </div>
                            <Button 
                                variant="ghost" 
                                size="icon" 
                                className="hidden sm:flex text-muted-foreground hover:text-destructive" 
                                onClick={() => removeFromCart(item.id, item.variantId)}
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </Card>
            )
          })}
           <div className="flex flex-col sm:flex-row justify-end gap-3 mt-4">
                <Button variant="outline" onClick={clearCart} className="w-full sm:w-auto order-2 sm:order-1">
                    Limpar Carrinho
                </Button>
                <Button asChild className="w-full sm:w-auto order-1 sm:order-2">
                    <Link href="/fastorder">
                        <Plus className="mr-2 h-4 w-4" /> Adicionar Produtos
                    </Link>
                </Button>
           </div>
        </div>
        
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="font-headline">Resumo do Pedido</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span>R$ {subtotal.toFixed(2)}</span>
              </div>
              {isVip && (
                <div className="space-y-1">
                  <div className="flex justify-between text-primary font-semibold">
                    <span className="flex items-center gap-1"><Star className="h-4 w-4 fill-primary" /> Desconto VIP</span>
                    <span>- R$ {vipDiscount.toFixed(2)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground text-right">Desconto VIP é válido para pagamentos na entrega (entrega agendada).</p>
                </div>
              )}
              <div className="flex justify-between text-muted-foreground">
                <span>Entrega</span>
                <span>A ser calculada</span>
              </div>
              <Separator />
              <div className="flex justify-between font-bold text-lg">
                <span>Total (parcial)</span>
                <span>R$ {total.toFixed(2)}</span>
              </div>
            </CardContent>
            <CardFooter>
              <Button size="lg" className="w-full" asChild disabled={outOfStockItems.length > 0}>
                <Link href={checkoutLink}>
                    Finalizar Compra <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
}
